import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { authorizedHttpPost } from "../api/httpCalls";
import urls from "../api/urls.json";
import { Environment } from "../env";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 0;
  ${(props) => props.compact && `
    margin: 0 0 0 8px;
    display: inline-flex;
    vertical-align: middle;
  `}
`;

const AddButton = styled.button`
  min-width: 84px;
  height: 38px;
  border: 1px solid #d7d7d7;
  border-radius: 8px;
  padding: 0 16px;
  font-family: "Gilory-Semi";
  font-size: 14px;
  color: #2f2f2f;
  background: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
`;

const CompactAddButton = styled(AddButton)`
  min-width: 64px;
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
`;

const DropdownMenu = styled.div`
  position: fixed;
  min-width: 180px;
  background: #ffffff;
  border: 1px solid #d7d7d7;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.16);
  overflow: hidden;
  z-index: 20;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: #ffffff;
  text-align: left;
  font-family: "Gilory-Semi";
  font-size: 13px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background: #f5f7fb;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const REASON_OPTIONS_BY_TAB = {
  people: ["Scammer", "Sus", "No Loc", "Trans", "Explicit"],
  updates: ["Scammer", "Sus", "Trans", "Explicit", "Edits", "Disapprove"],
  images: ["Scammer", "Sus", "Trans", "Explicit", "Edits"],
  autofeed: ["Scammer", "Sus", "No Loc", "Trans", "Explicit", "Edits"]
};

const getReasonsForSourceTab = (sourceTab) => {
  const normalizedTab = (sourceTab || "").toLowerCase();

  if (normalizedTab.startsWith("people-")) {
    return REASON_OPTIONS_BY_TAB.people;
  }

  if (normalizedTab === "updates") {
    return REASON_OPTIONS_BY_TAB.updates;
  }

  if (normalizedTab === "images") {
    return REASON_OPTIONS_BY_TAB.images;
  }

  if (normalizedTab.startsWith("autofeed-")) {
    return REASON_OPTIONS_BY_TAB.autofeed;
  }

  return [];
};

const BULK_SHARE_BATCH_SIZE = 10;
let pendingShareEntries = [];
let isBulkFlushInProgress = false;
let lastBulkFlushAtMs = 0;
const BULK_FLUSH_MIN_INTERVAL_MS = 1500;

const fetchBaseUrl = (endpoint) => {
  const appName = localStorage.getItem("appName");
  if (
    appName === "Aisle" ||
    endpoint === "events/create_event" ||
    endpoint === "events/all" ||
    endpoint === "events/update_event_state"
  ) {
    return Environment.is_development ? urls.TEST_BASE_URL : urls.PRODUCTION_BASE_URL;
  }
  return Environment.is_development ? urls.TEST_BASE_URL_ARIKE : urls.PRODUCTION_BASE_URL_ARIKE;
};

const pushShareEntry = (entry) => {
  const normalizedRef = String(entry.ref || "").trim();
  const existingEntryIndex = pendingShareEntries.findIndex(
    (pendingEntry) => String(pendingEntry.ref || "").trim() === normalizedRef
  );

  if (existingEntryIndex >= 0) {
    pendingShareEntries[existingEntryIndex] = entry;
    return;
  }

  pendingShareEntries.push(entry);
};

const flushPendingShareEntries = async () => {
  if (isBulkFlushInProgress || pendingShareEntries.length === 0) {
    return true;
  }

  isBulkFlushInProgress = true;
  const entriesToSend = pendingShareEntries.slice();

  return authorizedHttpPost(urls.CREATE_SHARING_ENTRIES_BULK, {
    entries: entriesToSend
  })
    .then((response) => {
      if (response && response.status) {
        pendingShareEntries = pendingShareEntries.slice(entriesToSend.length);
        return true;
      }

      return false;
    })
    .catch(() => false)
    .finally(() => {
      isBulkFlushInProgress = false;
    });
};

const triggerBulkFlushInBackground = () => {
  const nowMs = Date.now();
  if (isBulkFlushInProgress) {
    return;
  }
  if (nowMs - lastBulkFlushAtMs < BULK_FLUSH_MIN_INTERVAL_MS) {
    return;
  }
  lastBulkFlushAtMs = nowMs;

  // Intentionally fire-and-forget so sharing API issues never block major tab actions.
  flushPendingShareEntries().catch(() => {});
};

const flushPendingShareEntriesOnLeave = () => {
  if (pendingShareEntries.length === 0) {
    return;
  }

  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    return;
  }

  const payload = JSON.stringify({ entries: pendingShareEntries });
  const url = `${fetchBaseUrl(urls.CREATE_SHARING_ENTRIES_BULK)}${urls.CREATE_SHARING_ENTRIES_BULK}`;

  fetch(url, {
    method: "POST",
    headers: {
      Authorization: authToken,
      "Content-Type": "application/json",
      Accept: "application/json",
      "app-name": localStorage.getItem("appName"),
      webcms: true
    },
    body: payload,
    keepalive: true
  });
};

const ShareRefControl = ({ refValue, sourceTab, onShared, compact = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const reasonOptions = getReasonsForSourceTab(sourceTab);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const updateDropdownPosition = () => {
      if (!buttonRef.current) {
        return;
      }

      const buttonRect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + 6,
        left: buttonRect.left
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideWrapper = wrapperRef.current && wrapperRef.current.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);

      if (!clickedInsideWrapper && !clickedInsideDropdown) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const handlePageLeave = () => {
      flushPendingShareEntriesOnLeave();
    };

    window.addEventListener("beforeunload", handlePageLeave);
    window.addEventListener("pagehide", handlePageLeave);

    return () => {
      window.removeEventListener("beforeunload", handlePageLeave);
      window.removeEventListener("pagehide", handlePageLeave);
    };
  }, []);

  const doShare = async (reasonToShare) => {
    if (!refValue) {
      alert("No ref selected");
      return;
    }
    if (!reasonToShare) {
      alert("Please select a reason");
      return;
    }

    setIsSaving(true);
    pushShareEntry({
      ref: refValue,
      reason: reasonToShare,
      source_tab: sourceTab
    });
    triggerBulkFlushInBackground();

    setIsSaving(false);
    setIsDropdownOpen(false);
    if (onShared) {
      onShared();
    }
  };

  const handleReasonClick = async (reason) => {
    await doShare(reason);
  };

  return (
    <Wrapper compact={compact} ref={wrapperRef}>
      {compact ? (
        <CompactAddButton
          type="button"
          ref={buttonRef}
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          disabled={isSaving}
        >
          {isSaving ? "Adding..." : "Add"}
        </CompactAddButton>
      ) : (
        <AddButton
          type="button"
          ref={buttonRef}
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          disabled={isSaving}
        >
          {isSaving ? "Adding..." : "Add"}
        </AddButton>
      )}
      {isDropdownOpen && createPortal(
        <DropdownMenu
          ref={dropdownRef}
          style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
        >
          {reasonOptions.map((reason) => (
            <DropdownItem
              key={reason}
              type="button"
              disabled={isSaving}
              onClick={() => handleReasonClick(reason)}
            >
              {reason}
            </DropdownItem>
          ))}
        </DropdownMenu>,
        document.body
      )}
    </Wrapper>
  );
};

export default ShareRefControl;
